import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import { CircularProgress, Snackbar } from '@material-ui/core'
import axios from 'axios'
import BidEstimationHeader from '../shared/BidEstimationHeader'
import CategoryStage from './CategoryStage'
import ServiceStage from './ServiceStage'
import MaterialStage from './MaterialStage'
import AssemblySummary from './AssemblySummary'

const TABS = ['Categorize', 'Services', 'Materials', 'Summary']

const BidAssemblyPipeline = (props) => {
  const { projectId } = useParams()
  const { url } = props

  const [tab, setTab] = useState(0)
  const [assemblies, setAssemblies] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [project, setProject] = useState(null)
  const [bidPackages, setBidPackages] = useState([])
  const [services, setServices] = useState([])
  const [snack, setSnack] = useState('')

  const API = `${url}/api/bid-estimation`

  const fetchData = useCallback(async (silent) => {
    try {
      if (!silent) setLoading(true)
      const [asmRes, sumRes, projRes, pkgRes, svcRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/assembly`),
        axios.get(`${API}/projects/${projectId}/assembly/summary`),
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/bid-packages`),
        axios.get(`${API}/services`)
      ])
      setAssemblies(Array.isArray(asmRes.data) ? asmRes.data : [])
      setSummary(sumRes.data)
      setProject(projRes.data)
      setBidPackages(Array.isArray(pkgRes.data) ? pkgRes.data : [])
      setServices(Array.isArray(svcRes.data) ? svcRes.data : [])
    } catch (err) {
      console.error('BidAssemblyPipeline fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [API, projectId])

  useEffect(() => { fetchData(false) }, [fetchData])

  const refresh = () => fetchData(true)

  const handleInit = async (packageId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/assembly/init`, { packageId })
      setSnack('Assemblies initialized')
      refresh()
    } catch (err) {
      setSnack('Init failed: ' + (err.response?.data?.error || err.message))
    }
  }

  const handleAutoCategorize = async (packageId) => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/assembly/auto-categorize`, { packageId })
      setSnack(`Categorized ${res.data.categorized} of ${res.data.total} items`)
      refresh()
    } catch (err) {
      setSnack('Auto-categorize failed')
    }
  }

  const handleCalculateAll = async () => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/assembly/calculate-all`)
      setSnack(`Calculated ${res.data.calculated} of ${res.data.total} assemblies`)
      refresh()
    } catch (err) {
      setSnack('Calculate all failed')
    }
  }

  const handleSmoothEquipment = async () => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/assembly/smooth-equipment`)
      setSnack(`Smoothed ${res.data.adjustments} equipment allocations across ${res.data.equipmentGroups} groups`)
      refresh()
    } catch (err) {
      setSnack('Equipment smoothing failed')
    }
  }

  if (loading) {
    return <div className="hmi-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress /></div>
  }

  const total = summary?.total || 0
  const categorized = (summary?.byStatus?.categorized || 0) + (summary?.byStatus?.service_assigned || 0) +
    (summary?.byStatus?.materials_associated || 0) + (summary?.byStatus?.costed || 0) + (summary?.byStatus?.reviewed || 0)
  const serviceAssigned = (summary?.byStatus?.service_assigned || 0) + (summary?.byStatus?.materials_associated || 0) +
    (summary?.byStatus?.costed || 0) + (summary?.byStatus?.reviewed || 0)
  const materialsLinked = (summary?.byStatus?.materials_associated || 0) + (summary?.byStatus?.costed || 0) + (summary?.byStatus?.reviewed || 0)

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
      <BidEstimationHeader
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          { label: project?.name || 'Project', to: `/bid-estimation/${projectId}` },
          { label: 'Legacy Assembly' }
        ]}
        title="Legacy Bid Assembly Pipeline"
        actions={(
          <>
            <button className="hmi-btn hmi-btn-outline" onClick={handleCalculateAll}>Calculate all costs</button>
            <button className="hmi-btn hmi-btn-outline" onClick={handleSmoothEquipment}>Smooth equipment</button>
          </>
        )}
      />

      <div className="hmi-body">
      {/* Progress Bar */}
      <div className="prog-strip" style={{ marginBottom: 16 }}>
        <span className="prog-label">Pipeline progress</span>
        <div className="bars">
          <div className="seg"><i style={{ width: total > 0 ? `${(categorized / total) * 100}%` : '0%' }} /></div>
          <div className={`seg${serviceAssigned > 0 && serviceAssigned < total ? ' partial' : serviceAssigned === 0 ? ' empty' : ''}`}>
            <i style={{ width: total > 0 ? `${(serviceAssigned / total) * 100}%` : '0%' }} />
          </div>
          <div className={`seg${materialsLinked > 0 && materialsLinked < total ? ' partial' : materialsLinked === 0 ? ' empty' : ''}`}>
            <i style={{ width: total > 0 ? `${(materialsLinked / total) * 100}%` : '0%' }} />
          </div>
        </div>
        <div className="stats">
          <span><b>{categorized}</b>/{total} categorized</span>
          <span><b>{serviceAssigned}</b>/{total} service assigned</span>
          <span><b>{materialsLinked}</b>/{total} materials linked</span>
        </div>
      </div>

      {/* Init button if no assemblies */}
      {assemblies.length === 0 && bidPackages.length > 0 && (
        <div className="hmi-panel" style={{ textAlign: 'center', padding: 24, marginBottom: 16 }}>
          <p style={{ marginBottom: 12 }}>No assemblies yet. Initialize from a bid package to get started.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {bidPackages.map(pkg => (
              <button key={pkg._id} className="hmi-btn hmi-btn-primary" onClick={() => handleInit(pkg._id)}>
                Init from: {pkg.name || 'Package'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stage Tabs */}
      <div className="hmi-panel">
        <div className="hmi-tab-bar">
          {TABS.map((t, i) => (
            <button key={t} className={`hmi-tab${tab === i ? ' active' : ''}`} onClick={() => setTab(i)}>
              {t}
              {i === 0 && total > 0 && <span className="count">{total}</span>}
            </button>
          ))}
        </div>
        <div style={{ padding: 16 }}>
          {tab === 0 && <CategoryStage assemblies={assemblies} url={url} projectId={projectId} bidPackages={bidPackages} onAutoCategorize={handleAutoCategorize} onRefresh={refresh} />}
          {tab === 1 && <ServiceStage assemblies={assemblies} url={url} projectId={projectId} services={services} onRefresh={refresh} />}
          {tab === 2 && <MaterialStage assemblies={assemblies} url={url} projectId={projectId} onRefresh={refresh} />}
          {tab === 3 && <AssemblySummary assemblies={assemblies} url={url} projectId={projectId} summary={summary} />}
        </div>
      </div>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        message={snack}
      />
    </div></div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(BidAssemblyPipeline)
